import React, { useState } from 'react';
import { FiX, FiPlus, FiAlertCircle } from 'react-icons/fi';
import styles from '../Auth.module.css';

/**
 * ResearchInterestsInput - Free-form input for research interests with chip display
 */
const ResearchInterestsInput = ({
  selectedInterests = [],
  onInterestsChange,
  maxInterests = 15,
  suggestions = [],
  label = "Research Interests",
  placeholder = "Type and press Enter to add...",
  error,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Filter suggestions based on input
  const filteredSuggestions = suggestions
    .filter(s => 
      s.toLowerCase().includes(inputValue.toLowerCase()) && 
      !selectedInterests.includes(s)
    )
    .slice(0, 5);

  // Add interest
  const addInterest = (interest) => {
    const trimmed = interest.trim();
    if (
      trimmed && 
      !selectedInterests.includes(trimmed) && 
      selectedInterests.length < maxInterests
    ) {
      onInterestsChange([...selectedInterests, trimmed]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addInterest(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && selectedInterests.length > 0) {
      // Remove last interest on backspace when input is empty
      onInterestsChange(selectedInterests.slice(0, -1));
    }
  };

  // Remove interest
  const removeInterest = (interest) => {
    onInterestsChange(selectedInterests.filter(i => i !== interest));
  };

  const remainingCount = maxInterests - selectedInterests.length;

  return (
    <div className={styles['au-research-interests']}>
      {/* Header with count */}
      <div className={styles['au-interests-header']}>
        <label className={styles['au-input-group__label']}>{label}</label>
        <span className={`${styles['au-interests-count']} ${
          remainingCount === 0 ? styles['au-interests-count--full'] : ''
        }`}>
          {selectedInterests.length}/{maxInterests}
        </span>
      </div>

      {/* Input container with chips */}
      <div className={styles['au-research-interests__container']}>
        {/* Selected interests chips */}
        {selectedInterests.map((interest) => (
          <span key={interest} className={styles['au-interests-chip']}>
            {interest}
            <button
              type="button"
              className={styles['au-interests-chip__remove']}
              onClick={() => removeInterest(interest)}
              disabled={disabled}
            >
              <FiX size={12} />
            </button>
          </span>
        ))}

        {/* Input field */}
        {remainingCount > 0 && (
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(e.target.value.length > 0);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(inputValue.length > 0)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={selectedInterests.length === 0 ? placeholder : ''}
            className={styles['au-research-interests__input']}
            disabled={disabled}
          />
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className={styles['au-research-interests__suggestions']}>
          {filteredSuggestions.map((suggestion) => (
            <button
              type="button"
              key={suggestion}
              className={styles['au-research-interests__suggestion']}
              onMouseDown={(e) => {
                e.preventDefault();
                addInterest(suggestion);
              }}
            >
              <FiPlus size={12} />
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Help text */}
      <p className={styles['au-research-interests__help']}>
        Add your specific research topics. Press Enter to add each one.
      </p>

      {/* Error display */}
      {error && (
        <div className={styles['au-input-group__error']}>
          <FiAlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ResearchInterestsInput;
